import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "../../hooks";
import { api } from "../../api/api";
import { setClientId, setClientIdSent, setYMState, YMState } from "../../redux/app.slice";

interface IYandexMetrika {
    hit: (host: string, params: object) => void;
    reachGoal: (goalId: string, params?: object) => void;
    getClientID: () => string;
}

export enum YMGoals {
    OpenApp = 'open-app',
    GetWelcomeBonus = 'get-welcome-bonus',
    JoinChannelTask = 'join-channel-task',

    CompleteTutorialChallenges1 = 'complete-tutorial-challenges-1',
    CompleteTutorialChallenges2 = 'complete-tutorial-challenges-2',
    CompleteTutorialChallenges3 = 'complete-tutorial-challenges-3',
    CompleteTutorialChallenges4 = 'complete-tutorial-challenges-4',
    CompleteTutorialChallenges5 = 'complete-tutorial-challenges-5',
    CompleteTutorialChallenges6 = 'complete-tutorial-challenges-6',
    CompleteTutorialChallenges7 = 'complete-tutorial-challenges-7',
}

export const getYM = () => ((window as any)[`yaCounter${process.env.REACT_APP_YANDEX_METRIKA_COUNTER}`]) as IYandexMetrika;

interface ITimer {
    intervalId: any;
    count: number;
}

export const reachYMGoal = (goal: YMGoals) => {
    const ym = getYM();
    if (!ym)
        return;

    const hitParams = {
        title: document.title,
        referer: document.referrer,
    };

    ym.reachGoal(goal, hitParams);
}

export const YMHit = (yclid: string, pathname: string): string => {
    const ym = getYM();
    const hitParams = {
        title: document.title,
        referer: document.referrer,
    };

    const hitUrl = yclid ? `${pathname}?yclid=${yclid}` : pathname;
    ym?.hit(hitUrl, hitParams);
    return hitUrl;
}

export const YandexMetrika = () => {
    const location = useLocation();
    const dispatch = useDispatch();
    const { yclid, ymState, clientId, clientIdSent } = useSelector(store => store.appData);
    const { id: userId, loading: loginLoading, error: loginError } = useSelector(store => store.user.info);

    useEffect(() => {
        if (ymState !== YMState.Unknown)
            return;

        const checkReady = (timerObject: ITimer) => {
            const finish = (state: YMState) => {
                clearInterval(timerObject.intervalId);
                dispatch(setYMState(state));
            }

            if (getYM()) {
                finish(YMState.Ready);
                return;
            }

            if (++timerObject.count > 50) {
                finish(YMState.Fail);
                return;
            }
        };
    
        const timerObject: ITimer = { intervalId: null, count: 0 };
        timerObject.intervalId = setInterval(() => {
            checkReady(timerObject);
        }, 25);
        
        return () => clearInterval(timerObject.intervalId);
    }, [ymState, dispatch]);

    useEffect(() => {
        if (clientId && clientId.length > 0)
            return;

        // The counter may load after YMState.Fail or simply later on slow networks.
        const timerObject: ITimer = { intervalId: null, count: 0 };
        timerObject.intervalId = setInterval(() => {
            try {
                const nextClientId = getYM()?.getClientID?.();
                if (nextClientId && nextClientId.length > 0) {
                    dispatch(setClientId(nextClientId));
                    clearInterval(timerObject.intervalId);
                }
            }
            catch (e: unknown) {
                // ignore: metrika may be blocked/partially initialized
            }
            finally {
                // ~30 seconds max, then give up silently
                if (++timerObject.count > 60) {
                    clearInterval(timerObject.intervalId);
                }
            }
        }, 500);

        return () => clearInterval(timerObject.intervalId);
    }, [clientId, ymState, dispatch]);

    useEffect(() => {
        if (!userId)
            return;

        if (!clientId || clientId.length === 0)
            return;

        if (clientIdSent)
            return;

        let cancelled = false;
        (async () => {
            try {
                await api.auth.saveClientId(clientId);
                if (!cancelled) {
                    dispatch(setClientIdSent(true));
                }
            }
            catch (e: unknown) {
                // Not critical: may fail if endpoint not deployed yet or metrika blocked.
            }
        })();

        return () => { cancelled = true; };
    }, [userId, clientId, clientIdSent, dispatch]);

    useEffect(() => {
        if (ymState !== YMState.Ready)
            return;

        const loginSettled = !loginLoading && (userId > 0 || (loginError?.length ?? 0) > 0);
        const attributionReady = Boolean(yclid) || loginSettled;
        if (!attributionReady)
            return;

        YMHit(yclid, location.pathname);
        reachYMGoal(YMGoals.OpenApp);
    }, [location.pathname, location.search, yclid, ymState, userId, loginLoading, loginError]);

    return null;
};
